import React, { useState, useRef } from "react";
import "./Login.css";
import styles from "./Auth.module.css"; // Uncomment if you want to use the same logo style
import { GoogleLogin } from "@react-oauth/google";
import { Eye, EyeOff } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { authAPI } from "../../api/apiHelper";
import useToast from "../../hooks/useToast";
import Toast from "../../components/Toast";
import dec from "../../assets/landing/dec.svg";
import { Logo } from "../../components/logo/Logo";
import ReCAPTCHA from "react-google-recaptcha";


export const Signup = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();
  const { toast, success, error, hideToast } = useToast();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const tc = queryParams.get("TC");
  console.log("TC: ", tc);

  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeTlZQsAAAAADSV8zywpgg9tbFy5aX49r6FKbjY";

  const handleSignup = async (fullName, email, password) => {
    setIsLoading(true);
    try {
      const response = await authAPI.signup(
        fullName,
        email,
        password,
        null, // picture
        tc,   // TC
        recaptchaToken
      );

      // Store user data and token
      localStorage.setItem("authToken", response.token);
      localStorage.setItem(
        "userData",
        JSON.stringify({
          name: response.user.name,
          email: response.user.email,
          isOrganizationCreated: response.user.isOrganization,
          id: response.user.userId,
          role: response.user.role,
          teamType: response.user.teamType || null
        })
      );
      localStorage.setItem("orgId", response.user.organizationId);

      success("Account created successfully! Welcome to Galile.io!", {
        title: "Success",
      });

      // Reset reCAPTCHA
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }

      // Navigate to email verification page instead of onboarding
      navigate("/verify-email-pending");
    } catch (err) {
      console.error("Signup error:", err);

      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }

      if (err.response_code === 409) {
        error("Email already exists. Please try logging in instead.", {
          title: "Account Exists",
        });
      } else if (err.response_code === 422) {
        error("Invalid data provided. Please check your information.", {
          title: "Validation Error",
        });
      } else if (err.isNetworkError) {
        error("Network error. Please check your connection.", {
          title: "Connection Error",
        });
      } else {
        error(err.message || "Signup failed. Please try again.", {
          title: "Signup Failed",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleGoogleSignup = async (credentialResponse) => {
    setIsLoading(true);
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      console.log("Google user data:", decoded);

      const response = await authAPI.googleAuthV1(
        credentialResponse.credential,
        decoded.name,
        decoded.email,
        decoded.picture,
        tc
      );

      // Store user data and token
      localStorage.setItem("authToken", response.token);
      localStorage.setItem(
        "userData",
        JSON.stringify({
          name: response.user.name,
          email: response.user.email,
          isOrganizationCreated: response.user.organization,
          id: response.user._id,
          role: response.user.role,
          teamType: response.user.teamType || null
        })
      );
      localStorage.setItem("orgId", response.user.organizationId);
      success("Google signup successful! Welcome to Galile.io!", {
        title: "Success",
      });

      // Navigate to onboarding since this is a new account
      navigate("/onboarding");
    } catch (err) {
      console.error("Google signup error:", err);

      if (err.response_code === 409) {
        error("Account already exists. Please try logging in instead.", {
          title: "Account Exists",
        });
      } else if (err.response_code === 422) {
        error("Invalid data provided. Please try again.", {
          title: "Validation Error",
        });
      } else if (err.isNetworkError) {
        error("Network error. Please check your connection.", {
          title: "Connection Error",
        });
      } else {
        error(err.message || "Google signup failed. Please try again.", {
          title: "Signup Failed",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const trimmedName = fullName.trim();
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    const trimmedConfirm = confirmPassword.trim();

    if (!trimmedName || !trimmedEmail || !trimmedPassword || !trimmedConfirm) {
      error("Please fill in all fields.", { title: "Validation Error" });
      return;
    }

    if (trimmedPassword !== trimmedConfirm) {
      error("Passwords do not match.", { title: "Validation Error" });
      return;
    }

    if (trimmedPassword.length < 8) {
      error("Password must be at least 8 characters long.", {
        title: "Validation Error",
      });
      return;
    }

    if (!acceptTerms) {
      error("You must accept the Terms and Conditions.", {
        title: "Terms Required",
      });
      return;
    }

    if (!recaptchaToken) {
      error("Please complete the reCAPTCHA verification by checking the 'I'm not a robot' box.", { title: "reCAPTCHA Verification Required" });
      return;
    }

    await handleSignup(trimmedName, trimmedEmail, trimmedPassword);
  };

  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  return (
    <div className="login-root">
      <Toast
        open={ toast.open }
        message={ toast.message }
        severity={ toast.severity }
        title={ toast.title }
        duration={ toast.duration }
        onClose={ hideToast }
        position={ toast.position }
      />
      <div className="login-left-panel">
        <NavLink to="/">
          <Logo
            variant="white authLogo"
            className={styles.logo}
          />
        </NavLink>
        <img src={ dec } alt="Decorative background shape" className="svg_style" />
        <img src={ dec } alt="Decorative background shape" className="svg_style_1" />
        <div className="login-features">
          <div className="login-feature">
            <span className="login-check">✔</span>
            <div>
              <div className="login-feature-title">Seamless Transcription</div>
              <div className="login-feature-desc">
                Effortlessly transcribe audio to text with our advanced AI
                technology
              </div>
            </div>
          </div>
          <div className="login-feature">
            <span className="login-check">✔</span>
            <div>
              <div className="login-feature-title">Multi-language Support</div>
              <div className="login-feature-desc">
                Transcribe conversations in multiple languages with ease
              </div>
            </div>
          </div>
          <div className="login-feature">
            <span className="login-check">✔</span>
            <div>
              <div className="login-feature-title">Collaboration Tools</div>
              <div className="login-feature-desc">
                Collaborate with team members by sharing and annotating
                transcripts in real-time
              </div>
            </div>
          </div>
        </div>
        {/* Add logos here if needed */ }
      </div>
      <div className="login-right-panel">
        <form className="login-form" onSubmit={ onSubmit }>
          <div className="login-title">Create your account</div>
          <div className="login-subtitle">
            Experience the power of AI-driven transcripts
          </div>

          {/* singup with google but disable if tc param is present */ }


          {/* <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents:
                (tc && tc.trim() !== "") || isLoading ? "none" : "auto",
              opacity: (tc && tc.trim() !== "") || isLoading ? 0.5 : 1,
            }}
          >
            <GoogleLogin
              onSuccess={handleGoogleSignup}
              onError={() => {
                error("Google signup failed. Please try again.", {
                  title: "Signup Failed",
                });
              }}
              disabled={
                (!!tc && typeof tc === "string" && tc.trim() !== "") ||
                isLoading
              }
              text="continue_with"
            />
          </div> */}

          {/* signup with able to use google even if tc param is present */ }
          <div
            style={ {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: isLoading ? "none" : "auto",
              opacity: isLoading ? 0.5 : 1,
            } }
          >
            <GoogleLogin
              onSuccess={ handleGoogleSignup }
              onError={ () => {
                error("Google signup failed. Please try again.", {
                  title: "Signup Failed",
                });
              } }
              disabled={ isLoading }
              text="continue_with"
            />
          </div>

          <div className="login-divider-row">
            <div className="login-divider" />
            <span className="login-or">OR</span>
            <div className="login-divider" />
          </div>
          <div className="login-label">Full name</div>
          <input
            className="login-input"
            type="text"
            placeholder="Andrew Nolan"
            value={ fullName }
            onChange={ (e) => setFullName(e.target.value) }
            required
            disabled={ isLoading }
          />
          <div className="login-label">Email</div>
          <input
            className="login-input"
            type="email"
            placeholder="youremail@address.com"
            value={ email }
            onChange={ (e) => setEmail(e.target.value) }
            required
            disabled={ isLoading }
          />
          <div className="login-label">Password</div>
          <div style={ { position: "relative" } }>
            <input
              className="login-input"
              type={ showPassword ? "text" : "password" }
              placeholder="8+ characters required"
              value={ password }
              onChange={ (e) => setPassword(e.target.value) }
              required
              disabled={ isLoading }
            />
            <button
              type="button"
              onClick={ () => setShowPassword(!showPassword) }
              style={ {
                position: "absolute",
                right: "10px",
                top: "35%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0"
              } }
            >
              { showPassword ? <EyeOff size={ 20 } /> : <Eye size={ 20 } /> }
            </button>
          </div>
          <div className="login-label">Confirm password</div>
          <div style={ { position: "relative" } }>
            <input
              className="login-input"
              type={ showConfirmPassword ? "text" : "password" }
              placeholder="8+ characters required"
              value={ confirmPassword }
              onChange={ (e) => setConfirmPassword(e.target.value) }
              required
              disabled={ isLoading }
            />
            <button
              type="button"
              onClick={ () => setShowConfirmPassword(!showConfirmPassword) }
              style={ {
                position: "absolute",
                right: "10px",
                top: "35%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0"
              } }
            >
              { showConfirmPassword ? <EyeOff size={ 20 } /> : <Eye size={ 20 } /> }
            </button>
          </div>
          <div
            className="login-row login-remember-row"
            style={ { alignItems: "flex-start" } }
          >
            <label
              className="login-remember-label"
              style={ { alignItems: "flex-start" } }
            >
              <input
                type="checkbox"
                checked={ acceptTerms }
                onChange={ (e) => setAcceptTerms(e.target.checked) }
                style={ { marginTop: 2 } }
                disabled={ isLoading }
              />
              <span>
                I accept the{ " " }
                <a
                  href="#"
                  style={ { color: "#3949ab", textDecoration: "underline" } }
                >
                  Terms and Conditions
                </a>
              </span>
            </label>
          </div>
          
          {/* reCAPTCHA */ }
          <div style={ { display: 'flex', justifyContent: 'center', marginBottom: '16px' } }>
            <ReCAPTCHA
              ref={ recaptchaRef }
              sitekey={ RECAPTCHA_SITE_KEY }
              onChange={ onRecaptchaChange }
              onExpired={ () => setRecaptchaToken(null) }
              onError={ () => setRecaptchaToken(null) }
            />
          </div>
          
          <button
            type="submit"
            className="login-submit-btn"
            disabled={isLoading}
          >
            {isLoading ? "Creating account..." : "Sign up"}
          </button>
          <div className="login-signup-row">
            Already have an account?{ " " }
            <NavLink to={ "/login" } className="login-signup-link">
              Sign in
            </NavLink>
          </div>
        </form>
      </div>
    </div>
  );
};
